import { useState } from "react";

import Button from "components/ui/button/Button";
import { OpportunityDetailsResponse, ProviderTypes } from "lib/generated/integration-api";
import { useFindIntegrationProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { useOpportunityCrmDetails } from "lib/hooks/api/integrations/useOpportunityCrmDetails";
import { settingsRoutes } from "lib/routes";
import { EventNames } from "lib/tracking";
import { SendToCRMButton, ViewInCRMButton } from "./ProviderButtons";
import { SendOpportunityToCRMModal } from "./SendOpportunityToCRMModal";

type Opportunity = {
  guid: string;
  name: string;
  value?: number;
  createDate: string;
  closeDate?: string;
};

type SendOpportunityToCRMButtonProps = {
  opportunity: Opportunity;
  providerType: ProviderTypes;
  crmDetails?: OpportunityDetailsResponse;
};

export function SendOpportunityToCRM({
  opportunity,
}: {
  opportunity: Opportunity;
}): JSX.Element | null {
  const { data: providerTypeResponse, isLoading: isLoadingProvider } = useFindIntegrationProvider();
  const providerType = providerTypeResponse?.providerType;
  const { data: opportunityCrmDetails, isInitialLoading: isLoadingOpportunityCrmDetails } =
    useOpportunityCrmDetails(opportunity.guid, {
      enabled: !!providerType,
    });

  if (isLoadingProvider || isLoadingOpportunityCrmDetails) {
    return <Button buttonType="secondary" loading />;
  }

  return (
    <div>
      {providerType ? (
        <SendOpportunityToCRMButton
          opportunity={opportunity}
          providerType={providerType}
          crmDetails={opportunityCrmDetails}
        />
      ) : (
        <Button buttonType="secondary" as="a" href={settingsRoutes.integrations} icon="plus">
          CRM
        </Button>
      )}
    </div>
  );
}

function SendOpportunityToCRMButton({
  opportunity,
  crmDetails,
  providerType,
}: SendOpportunityToCRMButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onOpenChange = (open: boolean) => {
    setIsModalOpen(open);
  };

  if (crmDetails?.externalUrl) {
    return <ViewInCRMButton externalUrl={crmDetails.externalUrl} providerType={providerType} />;
  }

  return (
    <>
      <SendToCRMButton
        providerType={providerType}
        onClick={() => onOpenChange(true)}
        eventName={EventNames.sendToCRMModalOpened}
      />
      <SendOpportunityToCRMModal
        open={isModalOpen}
        onOpenChange={onOpenChange}
        opportunity={{
          guid: opportunity.guid,
          name: opportunity.name,
          value: opportunity.value,
          createDate: opportunity.createDate,
          closeDate: opportunity.closeDate,
        }}
        providerType={providerType}
      />
    </>
  );
}
